import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { ApiError, apiFetch } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { aiDsar, requestAiRtbf, type AiDsarExport, type AiRtbfResult } from '../ai-ops/aiOpsApi';
import { CommerceApprovalPanel } from '../commerce/CommerceApprovalPanel';
import type { CommerceOperation } from '../commerce/commerceApi';
import { roleLabel } from '../users/userModel';
import '../_shared/shared.css';
import './compliance.css';

interface DeletionPreflight {
  owned_devices?: { device_id: string; display_name?: string; shared_user_count?: number }[];
  owned_device_count?: number;
  shared_to_me_count?: number;
  active_subscriptions?: { device_id: string; status?: string }[];
  affected_shared_users?: { device_id: string; shared_user_count?: number }[];
  cooldown_days?: number;
  billing_retention_note?: string;
}

interface DeletionRequest {
  deletion_request_id: string;
  status: string;
  requested_at?: number;
  cooling_until_s?: number;
  cancelled_at?: number;
}

interface ExportJob {
  export_request_id: string;
  status: string;
  scope?: string[];
  format?: string;
  created_at?: number;
  ready_at?: number;
  expire_at_s?: number;
  size_bytes?: number;
}

interface ExportDownload {
  download_url: string;
  expires_in: number;
  content_type: string;
  size_bytes?: number;
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.code}: ${e.message}${e.requestId ? `（${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '操作失敗';
}

// 狀態文字 → pill 語意色調(涵蓋刪除申請 / 匯出 job 常見狀態字)
function statusPillClass(status: string | undefined): string {
  const s = (status || '').toLowerCase();
  if (['completed', 'done', 'ready', 'success', 'succeeded'].includes(s)) return 'pill-good';
  if (['failed', 'error', 'rejected', 'expired'].includes(s)) return 'pill-danger';
  if (['pending', 'cooling', 'processing', 'in_progress', 'requested'].includes(s)) return 'pill-warn';
  if (s === 'cancelled') return 'pill-neutral';
  return 'pill-info';
}

// 狀態 enum → 中文 label(比照全站 userStatusLabel 模式,pill 與 dl「狀態」dd 同源;
// 未知 enum 原樣通過,避免生吞未來新增狀態)。
const DELETION_STATUS_LABEL: Record<string, string> = {
  requested: '已申請',
  pending: '待處理',
  cooling: '冷卻期中',
  processing: '處理中',
  in_progress: '處理中',
  completed: '已完成',
  done: '已完成',
  cancelled: '已取消',
  rejected: '已拒絕',
  failed: '失敗',
  error: '錯誤',
  expired: '已逾期',
};

const EXPORT_STATUS_LABEL: Record<string, string> = {
  requested: '已申請',
  pending: '待處理',
  processing: '處理中',
  in_progress: '處理中',
  ready: '可下載',
  completed: '已完成',
  done: '已完成',
  success: '成功',
  succeeded: '成功',
  cancelled: '已取消',
  failed: '失敗',
  error: '錯誤',
  expired: '已逾期',
};

function deletionStatusLabel(status: string | undefined): string {
  if (!status) return '—';
  return DELETION_STATUS_LABEL[status.toLowerCase()] ?? status;
}

function exportStatusLabel(status: string | undefined): string {
  if (!status) return '—';
  return EXPORT_STATUS_LABEL[status.toLowerCase()] ?? status;
}

// 已填/未填徽章(案件情境條用)
function FilledBadge({ filled }: { filled: boolean }) {
  return (
    <span className={`pill compliance-pill-sm ${filled ? 'pill-good' : 'pill-neutral'}`}>{filled ? '已填' : '未填'}</span>
  );
}

export default function CompliancePage() {
  const { role } = useAuth();
  const writable = canAdminWrite(role);
  const [searchParams] = useSearchParams();
  // Customer-360 深連(/compliance?user_id=…)預填使用者 ID,比照 /audit 讀 case_id query 的 pattern。
  const [userId, setUserId] = useState(() => searchParams.get('user_id') ?? '');
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');
  const [exportId, setExportId] = useState('');
  const [preflight, setPreflight] = useState<DeletionPreflight | null>(null);
  const [deletion, setDeletion] = useState<DeletionRequest | null>(null);
  const [exportJob, setExportJob] = useState<ExportJob | null>(null);
  const [download, setDownload] = useState<ExportDownload | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [err, setErr] = useState('');
  // 二次確認彈窗:'delete' | 'cancel' | null。不可逆(代理刪除)與逆向(取消刪除)動作
  // 都先過這一關,確認框內重述 preflight 風險摘要,強化「這是寫入動作」的儀式感。
  const [confirmAction, setConfirmAction] = useState<'delete' | 'cancel' | null>(null);

  // AI 對話資料 RTBF/DSAR(R19 Phase-1)。owner 預填 Customer-360 帶入的 user_id(複用同一個值),
  // 但可編輯成完整資料主體字串(user:<uid> / external:<帳號> …),cs 依此 owner 定位。
  const [aiOwner, setAiOwner] = useState(() => searchParams.get('user_id') ?? '');
  const [aiExport, setAiExport] = useState<AiDsarExport | null>(null);
  // RTBF 是受治理動作:本頁只建立待覆核操作(maker),刪除筆數要等另一位管理員
  // 覆核執行後,才由 operation.result 帶回。
  const [rtbfOperation, setRtbfOperation] = useState<CommerceOperation | null>(null);
  const [aiRtbfResult, setAiRtbfResult] = useState<AiRtbfResult | null>(null);
  // RTBF 二段確認:先過 canAdminWrite + 案件情境閘,開彈窗後還需鍵入 owner 完全一致才解鎖確認鈕。
  const [confirmRtbf, setConfirmRtbf] = useState(false);
  const [rtbfConfirmText, setRtbfConfirmText] = useState('');

  const uid = userId.trim();
  const owner = aiOwner.trim();
  const body = () => ({ reason: reason.trim(), case_id: caseId.trim() });

  async function run<T>(fn: () => Promise<T>, done: (v: T) => void, ok: string) {
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const value = await fn();
      done(value);
      setNotice(ok);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  function requireContext(): boolean {
    return Boolean(reason.trim() && caseId.trim());
  }

  const contextReady = Boolean(uid) && requireContext();

  async function doDelete() {
    await run(
      async () =>
        (
          await apiFetch<DeletionRequest>(`/v3/admin/users/${encodeURIComponent(uid)}/deletion-request`, {
            method: 'POST',
            body: body(),
          })
        ).data,
      setDeletion,
      '已送出刪除申請。'
    );
    setConfirmAction(null);
  }

  async function doCancel() {
    await run(
      async () =>
        (
          await apiFetch<{ status: string }>(`/v3/admin/users/${encodeURIComponent(uid)}/deletion-cancel`, {
            method: 'POST',
            body: body(),
          })
        ).data,
      // 採用 server 回傳的實際狀態,不硬寫 'cancelled'(避免後端未取消卻謊報已取消)
      (res) => setDeletion((d) => (d ? { ...d, status: res?.status || d.status } : d)),
      '已取消刪除申請。'
    );
    setConfirmAction(null);
  }

  // DSAR 匯出(讀):任何管理員可讀;僅需 owner。
  async function doDsar() {
    await run(async () => aiDsar(owner), setAiExport, '已匯出 AI 對話資料。');
  }

  // 匯出結果下載成 JSON 檔(前端組 blob,不另打 API)。
  function downloadDsar() {
    if (!aiExport) return;
    const blob = new Blob([JSON.stringify(aiExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-dsar-${owner || 'owner'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // RTBF 刪除(寫,不可逆):建立待覆核操作,不直接執行 —— 實際刪除由另一位
  // 管理員在下方雙人覆核面板核准(common/12 D14④/⑤)。
  async function doRtbf() {
    await run(
      async () => requestAiRtbf(owner, { reason: reason.trim(), case_id: caseId.trim() }),
      (op) => {
        setRtbfOperation(op);
        setAiRtbfResult(null);
      },
      '已建立待覆核的 RTBF 操作;需由另一位管理員覆核後才會執行刪除。'
    );
    setConfirmRtbf(false);
    setRtbfConfirmText('');
  }

  // RTBF 寫入的案件情境閘:owner + 原因 + 案件編號皆備妥。
  const aiContextReady = Boolean(owner) && requireContext();

  const cooldownDays = preflight?.cooldown_days ?? 0;
  const subCount = preflight?.active_subscriptions?.length ?? 0;
  const sharedCount = preflight?.shared_to_me_count ?? 0;

  return (
    <div className="compliance-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">帳號刪除 / DSAR</h1>
          <p className="page-subtitle">管理員代理使用者的帳號刪除與資料匯出申請；寫入動作必須填寫原因與案件編號。</p>
        </div>
        <span className={`pill ${writable ? 'pill-good' : 'pill-neutral'}`}>{roleLabel(role ?? undefined)}</span>
      </div>

      {/* 案件情境條:貫穿全寬、常駐顯示目前鎖定的案件情境,往下捲動也不需回頭確認 */}
      <div className="adm-case-banner">
        <div className="adm-case-banner-field">
          <span className="compliance-case-label-row">
            <span className="adm-case-banner-label">使用者 ID</span>
            <FilledBadge filled={Boolean(uid)} />
          </span>
          <input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="u-us-..." />
        </div>
        <div className="adm-case-banner-field">
          <span className="compliance-case-label-row">
            <span className="adm-case-banner-label">原因</span>
            <FilledBadge filled={Boolean(reason.trim())} />
          </span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={!writable || busy} />
        </div>
        <div className="adm-case-banner-field">
          <span className="compliance-case-label-row">
            <span className="adm-case-banner-label">案件編號</span>
            <FilledBadge filled={Boolean(caseId.trim())} />
          </span>
          <input value={caseId} onChange={(e) => setCaseId(e.target.value)} disabled={!writable || busy} />
        </div>
      </div>

      {notice && <p className="adm-action-result is-ok" role="status">{notice}</p>}
      {err && <p className="adm-action-result is-err" role="alert">{err}</p>}

      <section className="card card--primary">
        <div className="section-head">
          <h2 className="section-title">刪除流程</h2>
          {deletion && <span className={`pill ${statusPillClass(deletion.status)}`}>{deletionStatusLabel(deletion.status)}</span>}
        </div>

        {/* 唯讀查詢區 */}
        <div className="adm-action-section">
          <div className="adm-actionbar-btns">
            <button
              className="btn-ghost"
              disabled={busy || !uid}
              onClick={() =>
                void run(
                  async () => (await apiFetch<DeletionPreflight>(`/v3/admin/users/${encodeURIComponent(uid)}/deletion-preflight`)).data,
                  setPreflight,
                  '已取得刪除前檢查。'
                )
              }
            >
              刪除前檢查
            </button>
            <button
              className="btn-ghost"
              disabled={busy || !uid}
              onClick={() =>
                void run(
                  async () => (await apiFetch<DeletionRequest>(`/v3/admin/users/${encodeURIComponent(uid)}/deletion-status`)).data,
                  setDeletion,
                  '已取得刪除狀態。'
                )
              }
            >
              狀態
            </button>
          </div>

          {preflight ? (
            <dl className="adm-kv compliance-risk-kv">
              <dt>自有裝置</dt>
              <dd>{preflight.owned_device_count ?? preflight.owned_devices?.length ?? 0}</dd>
              <dt>共享給我</dt>
              <dd>{sharedCount > 0 ? <span className="pill pill-warn">{sharedCount} 位（有共享關係）</span> : '0'}</dd>
              <dt>冷卻期</dt>
              <dd>{cooldownDays > 0 ? <span className="pill pill-warn">{cooldownDays} 天（尚在冷卻期）</span> : '無冷卻限制'}</dd>
              <dt>訂閱</dt>
              <dd>{subCount > 0 ? <span className="pill pill-warn">{subCount} 筆使用中</span> : '0'}</dd>
              <dt>備註</dt>
              <dd>{preflight.billing_retention_note || '—'}</dd>
            </dl>
          ) : (
            <div className="empty-state empty-state--inline">
              <div className="empty-state-icon">🔍</div>
              <div className="empty-state-detail">尚未查詢刪除前檢查 → 點擊「刪除前檢查」開始。</div>
            </div>
          )}
        </div>

        {/* 寫入動作區:淡紅底 + 上分隔線,與唯讀查詢明確分區 */}
        <div className="adm-danger-zone">
          <div className="adm-risk-group">
            <button
              className="btn-danger"
              // 代理刪除前強制先成功跑一次刪除前檢查 → 操作者必看過影響面(裝置/共享/訂閱)才能送出
              disabled={!writable || busy || !contextReady || !preflight}
              onClick={() => setConfirmAction('delete')}
            >
              代理刪除
            </button>
            <button
              className="btn-ghost-danger"
              disabled={!writable || busy || !contextReady}
              onClick={() => setConfirmAction('cancel')}
            >
              取消刪除
            </button>
          </div>
          {writable && contextReady && !preflight && (
            <p className="text-tertiary" style={{ marginTop: 'var(--spacing-2)' }}>
              代理刪除前請先執行「刪除前檢查」,確認裝置 / 共享 / 訂閱影響面後才可送出。
            </p>
          )}

          {deletion ? (
            <dl className="adm-kv">
              <dt>申請編號</dt>
              <dd className="adm-mono">{deletion.deletion_request_id}</dd>
              <dt>狀態</dt>
              <dd>{deletionStatusLabel(deletion.status)}</dd>
              <dt>申請時間</dt>
              <dd className="adm-mono">{formatTimestampUs(deletion.requested_at)}</dd>
              <dt>冷卻至</dt>
              <dd className="adm-mono">{formatTimestampUs(deletion.cooling_until_s ? deletion.cooling_until_s * 1_000_000 : undefined)}</dd>
            </dl>
          ) : (
            <div className="empty-state empty-state--inline">
              <div className="empty-state-icon">🗑️</div>
              <div className="empty-state-detail">尚未送出刪除申請。填妥案件情境後可代理刪除。</div>
            </div>
          )}
        </div>
      </section>

      <section className="card card--primary">
        <div className="section-head">
          <h2 className="section-title">DSAR 資料匯出</h2>
          {exportJob && <span className={`pill ${statusPillClass(exportJob.status)}`}>{exportStatusLabel(exportJob.status)}</span>}
        </div>

        {/* 建立簇(寫入):以目前案件情境建立新匯出 */}
        <div className="adm-action-section">
          <h3 className="adm-subtitle">建立新匯出</h3>
          <div className="compliance-export-create">
            <button
              className="btn-primary"
              disabled={!writable || busy || !contextReady}
              onClick={() =>
                void run(
                  async () =>
                    (
                      await apiFetch<ExportJob>(`/v3/admin/users/${encodeURIComponent(uid)}/export`, {
                        method: 'POST',
                        body: { ...body(), scope: [], format: 'json' },
                      })
                    ).data,
                  (job) => {
                    setExportJob(job);
                    setExportId(job.export_request_id);
                    setDownload(null);
                  },
                  '已建立資料匯出案件。'
                )
              }
            >
              建立匯出
            </button>
            <span className="adm-inline-note text-secondary compliance-flush">
              以目前案件情境建立一份新的 DSAR 匯出案件。
            </span>
          </div>
        </div>

        {/* 檢視簇(唯讀):以匯出 ID 查詢既有案件並取得下載連結 */}
        <div className="adm-action-section">
          <h3 className="adm-subtitle">檢視既有匯出</h3>
          <div className="compliance-export-form">
            <label className="adm-field compliance-export-idfield">
              <span className="adm-field-label text-secondary">匯出 ID</span>
              <input value={exportId} onChange={(e) => setExportId(e.target.value)} placeholder="exp-..." />
            </label>
            <button
              className="btn-ghost"
              disabled={!writable || busy || !uid || !exportId.trim()}
              onClick={() =>
                void run(
                  async () =>
                    (
                      await apiFetch<ExportJob>(
                        `/v3/admin/users/${encodeURIComponent(uid)}/export/${encodeURIComponent(exportId.trim())}`
                      )
                    ).data,
                  setExportJob,
                  '已取得匯出案件狀態。'
                )
              }
            >
              查詢
            </button>
            <button
              className="btn-ghost"
              disabled={!writable || busy || !uid || !exportId.trim()}
              onClick={() =>
                void run(
                  async () =>
                    (
                      await apiFetch<ExportDownload>(
                        `/v3/admin/users/${encodeURIComponent(uid)}/export/${encodeURIComponent(exportId.trim())}/download`
                      )
                    ).data,
                  setDownload,
                  '已產生下載連結。'
                )
              }
            >
              下載連結
            </button>
          </div>

          {exportJob ? (
            <dl className="adm-kv">
              <dt>匯出編號</dt>
              <dd className="adm-mono">{exportJob.export_request_id}</dd>
              <dt>狀態</dt>
              <dd>{exportStatusLabel(exportJob.status)}</dd>
              <dt>格式</dt>
              <dd>{exportJob.format || '—'}</dd>
              <dt>建立時間</dt>
              <dd className="adm-mono">{formatTimestampUs(exportJob.created_at)}</dd>
            </dl>
          ) : (
            <div className="empty-state empty-state--inline">
              <div className="empty-state-icon">📦</div>
              <div className="empty-state-detail">尚未建立任何匯出案件。填妥案件情境後可建立 DSAR 匯出。</div>
            </div>
          )}

          {download && (
            <div className="adm-secret-box adm-secret-box--info">
              <p className="adm-secret-warn">短效下載連結（非長期機密，逾期即失效）</p>
              <a href={download.download_url} target="_blank" rel="noreferrer" className="adm-secret-value">
                {download.download_url}
              </a>
              <p className="text-secondary compliance-flush">
                有效期 {download.expires_in} 秒・{download.content_type}
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="card card--primary" data-testid="ai-section">
        <div className="section-head">
          <h2 className="section-title">AI 對話資料(RTBF / DSAR)</h2>
          {aiRtbfResult ? (
            <span className="pill pill-danger">已刪除</span>
          ) : rtbfOperation ? (
            <span className="pill pill-warn">待覆核</span>
          ) : null}
        </div>
        <p className="adm-inline-note text-secondary compliance-flush">
          針對 AI 客服 / 管家(Aura)留存的對話資料:DSAR 匯出某資料主體的全部對話輪次、轉接與記憶;RTBF 則不可逆地刪除之。
          資料主體(owner）形如 <span className="adm-mono">{'user:<uid>'}</span> / <span className="adm-mono">{'external:<帳號>'}</span>,預填為 Customer-360 帶入的使用者 ID,可再補上前綴。
        </p>

        {/* 資料主體 owner:與帳號刪除區的使用者 ID 分開,因 cs owner 需帶前綴語意 */}
        <label className="adm-field" data-testid="ai-owner-field">
          <span className="adm-field-label text-secondary">資料主體（owner）</span>
          <input
            value={aiOwner}
            onChange={(e) => setAiOwner(e.target.value)}
            placeholder="user:u-us-… / external:…"
            data-testid="ai-owner-input"
          />
        </label>

        {/* DSAR 匯出(讀):任何管理員可讀取匯出 */}
        <div className="adm-action-section">
          <h3 className="adm-subtitle">DSAR 匯出</h3>
          <div className="adm-actionbar-btns">
            <button
              className="btn-ghost"
              disabled={busy || !owner}
              onClick={() => void doDsar()}
              data-testid="ai-dsar-btn"
            >
              匯出 AI 對話資料
            </button>
            {aiExport && (
              <button className="btn-ghost" disabled={busy} onClick={downloadDsar} data-testid="ai-dsar-download-btn">
                下載 JSON
              </button>
            )}
          </div>

          {aiExport ? (
            <dl className="adm-kv" data-testid="ai-dsar-summary">
              <dt>資料主體</dt>
              <dd className="adm-mono">{aiExport.owner}</dd>
              <dt>對話</dt>
              <dd>{aiExport.conversations.length} 筆</dd>
              <dt>對話輪次</dt>
              <dd>{aiExport.traces.length} 筆</dd>
              <dt>評價</dt>
              <dd>{aiExport.feedback.length} 筆</dd>
            </dl>
          ) : (
            <div className="empty-state empty-state--inline">
              <div className="empty-state-icon">📤</div>
              <div className="empty-state-detail">尚未匯出。輸入資料主體後可匯出其全部 AI 對話資料。</div>
            </div>
          )}
        </div>

        {/* RTBF 刪除(寫,不可逆):淡紅底危險區,唯讀角色隱藏。
            本區只「申請」——實際執行需另一位管理員在下方面板覆核。 */}
        {writable && (
          <div className="adm-danger-zone">
            <h3 className="adm-subtitle">RTBF 刪除（不可逆・需雙人覆核）</h3>
            <div className="adm-risk-group">
              <button
                className="btn-danger"
                disabled={busy || !aiContextReady}
                onClick={() => {
                  setRtbfConfirmText('');
                  setConfirmRtbf(true);
                }}
                data-testid="ai-rtbf-btn"
              >
                申請刪除全部 AI 對話資料
              </button>
              <span className="adm-inline-note text-secondary compliance-flush">
                需先填妥案件情境（原因 + 案件編號）。送出後**不會立即刪除**：需由另一位管理員以密碼覆核後才執行,操作 24 小時後失效。
              </span>
            </div>

            {rtbfOperation && !aiRtbfResult && (
              <dl className="adm-kv" data-testid="ai-rtbf-operation">
                <dt>待覆核操作</dt>
                <dd className="adm-mono">{rtbfOperation.operation_id}</dd>
                <dt>資料主體</dt>
                <dd className="adm-mono">{owner}</dd>
                <dt>狀態</dt>
                <dd>{rtbfOperation.status === 'pending' ? '待另一位管理員覆核' : rtbfOperation.status}</dd>
              </dl>
            )}

            {aiRtbfResult ? (
              <dl className="adm-kv" data-testid="ai-rtbf-result">
                <dt>資料主體</dt>
                <dd className="adm-mono">{aiRtbfResult.owner}</dd>
                <dt>對話</dt>
                <dd>{aiRtbfResult.deleted.conversations} 筆</dd>
                <dt>對話輪次</dt>
                <dd>{aiRtbfResult.deleted.traces} 筆</dd>
                <dt>評價</dt>
                <dd>{aiRtbfResult.deleted.feedback} 筆</dd>
                {aiRtbfResult.deleted.butler_memory !== undefined && (
                  <>
                    <dt>管家記憶</dt>
                    <dd>{aiRtbfResult.deleted.butler_memory} 筆</dd>
                  </>
                )}
                {aiRtbfResult.deleted.handoffs !== undefined && (
                  <>
                    <dt>轉接</dt>
                    <dd>{aiRtbfResult.deleted.handoffs} 筆</dd>
                  </>
                )}
              </dl>
            ) : !rtbfOperation ? (
              <div className="empty-state empty-state--inline">
                <div className="empty-state-icon">🗑️</div>
                <div className="empty-state-detail">尚未申請。填妥案件情境後可申請刪除該資料主體的全部 AI 對話資料;實際刪除需雙人覆核。</div>
              </div>
            ) : null}
          </div>
        )}
      </section>

      {/* 雙人覆核:與 commerce 治理動作共用同一面板與同一組 API。
          覆核者必須是不同管理員(伺服端強制),完成後把刪除筆數回填上方。 */}
      {writable && rtbfOperation && (
        <CommerceApprovalPanel
          operationId={rtbfOperation.operation_id}
          onCompleted={(op) => {
            setRtbfOperation(op);
            if (op.result) setAiRtbfResult(op.result as unknown as AiRtbfResult);
          }}
        />
      )}

      {confirmRtbf && (
        <ConfirmDialog
          title="確認刪除全部 AI 對話資料"
          tone="danger"
          warning="此動作不可逆:將永久刪除該資料主體在 AI 客服 / 管家留存的全部對話輪次、轉接與記憶,無法復原。"
          body={
            <div>
              <p className="compliance-confirm-lead">
                即將對資料主體 <span className="adm-mono">{owner}</span> 執行 RTBF,連帶刪除:
              </p>
              <ul>
                <li>全部對話與對話輪次</li>
                <li>對話評價（feedback）</li>
                <li>轉真人客服轉接單與往返訊息（handoffs）</li>
                <li>管家（Aura）長期記憶（butler_memory）</li>
              </ul>
              <dl className="adm-kv">
                <dt>案件編號</dt>
                <dd className="adm-mono">{caseId.trim()}</dd>
                <dt>原因</dt>
                <dd>{reason.trim()}</dd>
              </dl>
              <label className="adm-field" data-testid="ai-rtbf-confirm-field">
                <span className="adm-field-label text-secondary">
                  請鍵入資料主體 <span className="adm-mono">{owner}</span> 以確認
                </span>
                <input
                  value={rtbfConfirmText}
                  onChange={(e) => setRtbfConfirmText(e.target.value)}
                  placeholder={owner}
                  data-testid="ai-rtbf-confirm-input"
                />
              </label>
            </div>
          }
          confirmLabel="確認永久刪除"
          confirmDisabled={rtbfConfirmText.trim() !== owner}
          busy={busy}
          error={err || undefined}
          onConfirm={() => void doRtbf()}
          onCancel={() => {
            setConfirmRtbf(false);
            setRtbfConfirmText('');
          }}
        />
      )}

      {confirmAction === 'delete' && (
        <ConfirmDialog
          title="確認代理刪除帳號"
          tone="danger"
          warning="此動作為不可逆的帳號刪除申請，一旦送出將依冷卻期排程執行。"
          body={
            <div>
              <p className="compliance-confirm-lead">使用者 {uid} 的刪除前檢查摘要：</p>
              <dl className="adm-kv compliance-risk-kv">
                <dt>自有裝置</dt>
                <dd>{preflight?.owned_device_count ?? preflight?.owned_devices?.length ?? '尚未查詢'}</dd>
                <dt>冷卻期</dt>
                <dd>{cooldownDays > 0 ? `${cooldownDays} 天` : preflight ? '無冷卻限制' : '尚未查詢'}</dd>
                <dt>訂閱</dt>
                <dd>{preflight ? `${subCount} 筆使用中` : '尚未查詢'}</dd>
                <dt>案件編號</dt>
                <dd className="adm-mono">{caseId.trim()}</dd>
                <dt>原因</dt>
                <dd>{reason.trim()}</dd>
              </dl>
            </div>
          }
          confirmLabel="確認代理刪除"
          busy={busy}
          error={err || undefined}
          onConfirm={() => void doDelete()}
          onCancel={() => setConfirmAction(null)}
        />
      )}
      {confirmAction === 'cancel' && (
        <ConfirmDialog
          title="確認取消刪除申請"
          tone="warn"
          body={
            <div>
              <p className="compliance-confirm-lead">將取消使用者 {uid} 目前的刪除申請。</p>
              <dl className="adm-kv">
                <dt>案件編號</dt>
                <dd className="adm-mono">{caseId.trim()}</dd>
                <dt>原因</dt>
                <dd>{reason.trim()}</dd>
              </dl>
            </div>
          }
          confirmLabel="確認取消刪除"
          busy={busy}
          error={err || undefined}
          onConfirm={() => void doCancel()}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
